import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./components/UI/Toast";
import { useAnalytics } from "./hooks/useAnalytics";
import ModernNavbar from "./components/Navigation/ModernNavbar";
import Footer from "./components/Layout/Footer";
import HomePage from './components/Home/HomePage';
// import DefaultSEO from './components/SEO/DefaultSEO'; // Disabled - causes title conflicts
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import "./styles/animations.css";
import "./styles/globals.css";
import "./styles/universal-cards.css";
import "./styles/mobile-responsive.css";
import "./styles/style-corrections.css";

// Lazy load components for better performance
const About = React.lazy(() => import("./components/Pages/About"));
const Contact = React.lazy(() => import("./components/Pages/Contact"));
const PrivacyPolicy = React.lazy(
  () => import("./components/Pages/PrivacyPolicy")
);
const TermsOfService = React.lazy(
  () => import("./components/Pages/TermsOfService")
);
const AffiliateDisclosure = React.lazy(
  () => import("./components/Pages/AffiliateDisclosure")
);
const HelpCenter = React.lazy(() => import("./components/Pages/HelpCenter"));
const DynamicPage = React.lazy(() => import("./components/Pages/DynamicPage"));
const Login = React.lazy(() => import("./components/Auth/Login"));
const Signup = React.lazy(() => import("./components/Auth/Signup"));
const SinglePost = React.lazy(() => import("./components/Post/SinglePost"));
const RecentPosts = React.lazy(() => import("./components/Post/RecentPosts"));
const PopularPosts = React.lazy(() => import("./components/Post/PopularPosts"));
const FeaturedPosts = React.lazy(
  () => import("./components/Post/FeaturedPosts")
);
const UserProfile = React.lazy(() => import("./components/User/UserProfile"));
const CategoryPage = React.lazy(
  () => import("./components/Category/CategoryPage")
);
const Categories = React.lazy(() => import("./components/Category/Categories"));
const SearchResults = React.lazy(
  () => import("./components/Search/SearchResults")
);
const TagsPage = React.lazy(() => import("./components/Tags/TagsPage"));
const AdminDashboard = React.lazy(
  () => import("./components/Admin/AdminDashboard")
);
const AdminRoleSetter = React.lazy(
  () => import("./components/Admin/AdminRoleSetter")
);

// Component to initialize analytics inside Router context
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useAnalytics();
  return <>{children}</>;
};

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
      fontSize: "16px",
      color: "#666",
    }}
  >
    Loading...
  </div>
);

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AnalyticsWrapper>
            <div className="App">
              <ModernNavbar />
              <main>
                {/* DefaultSEO completely disabled - each page handles its own SEO */}
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/terms-of-service"
                      element={<TermsOfService />}
                    />
                    <Route
                      path="/affiliate-disclosure"
                      element={<AffiliateDisclosure />}
                    />
                    <Route path="/help-center" element={<HelpCenter />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/recent" element={<RecentPosts />} />
                    <Route path="/popular" element={<PopularPosts />} />
                    <Route path="/featured" element={<FeaturedPosts />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/profile" element={<UserProfile />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route path="/tag/:tagName" element={<TagsPage />} />
                    <Route path="/set-admin" element={<AdminRoleSetter />} />
                    <Route path="/post/:id" element={<SinglePost />} />
                    <Route
                      path="/category/:categorySlug"
                      element={<CategoryPage />}
                    />
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/* Catch-all route for dynamic pages created in admin */}
                    <Route path=":slug" element={<DynamicPage />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </AnalyticsWrapper>
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
